/**
 * AppContainer - Root layout component for tool UIs
 *
 * Replaces pointer-events hacks with proper CSS Grid layout.
 * All children render in-document (no z-index layering hacks).
 */

import React, { useState, useCallback, useEffect } from 'react';
import { Panel } from './Panel';
import { LayoutContext, PanelConfig, LayoutState } from './LayoutContext';
import './Layout.css';

interface AppContainerProps {
  children: React.ReactNode;
  header?: React.ReactNode;
  leftPanel?: React.ReactNode;
  rightPanel?: React.ReactNode;
  bottomPanel?: React.ReactNode;
  className?: string;
  /** Storage key for persisting layout */
  storageKey?: string;
}

const DEFAULT_LEFT_PANEL: PanelConfig = {
  width: 280,
  collapsed: false,
  minWidth: 200,
  maxWidth: 800,
};

const DEFAULT_RIGHT_PANEL: PanelConfig = {
  width: 280,
  collapsed: false,
  minWidth: 200,
  maxWidth: 800,
};

export const AppContainer: React.FC<AppContainerProps> = ({
  children,
  header,
  leftPanel,
  rightPanel,
  bottomPanel,
  className = '',
  storageKey = 'helix-layout',
}) => {
  const [isInteractingWithUI, setIsInteractingWithUI] = useState(false);

  // Load persisted state
  const [layoutState, setLayoutState] = useState<LayoutState>(() => {
    if (typeof window === 'undefined') {
      return {
        leftPanel: DEFAULT_LEFT_PANEL,
        rightPanel: DEFAULT_RIGHT_PANEL,
      };
    }
    try {
      const saved = localStorage.getItem(storageKey);
      if (saved) {
        const parsed = JSON.parse(saved);
        return {
          leftPanel: { ...DEFAULT_LEFT_PANEL, ...parsed.leftPanel },
          rightPanel: { ...DEFAULT_RIGHT_PANEL, ...parsed.rightPanel },
        };
      }
    } catch (e) {
      console.warn('Failed to load layout state:', e);
    }
    return {
      leftPanel: DEFAULT_LEFT_PANEL,
      rightPanel: DEFAULT_RIGHT_PANEL,
    };
  });

  // Persist state on change
  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      localStorage.setItem(storageKey, JSON.stringify(layoutState));
    } catch (e) {
      console.warn('Failed to save layout state:', e);
    }
  }, [layoutState, storageKey]);

  const updateLeftPanel = useCallback((updates: Partial<PanelConfig>) => {
    setLayoutState(prev => ({
      ...prev,
      leftPanel: { ...prev.leftPanel, ...updates },
    }));
  }, []);

  const updateRightPanel = useCallback((updates: Partial<PanelConfig>) => {
    setLayoutState(prev => ({
      ...prev,
      rightPanel: { ...prev.rightPanel, ...updates },
    }));
  }, []);

  const toggleLeftPanel = useCallback(() => {
    setLayoutState(prev => ({
      ...prev,
      leftPanel: {
        ...prev.leftPanel,
        collapsed: !prev.leftPanel.collapsed,
      },
    }));
  }, []);

  const toggleRightPanel = useCallback(() => {
    setLayoutState(prev => ({
      ...prev,
      rightPanel: {
        ...prev.rightPanel,
        collapsed: !prev.rightPanel.collapsed,
      },
    }));
  }, []);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Toggle left panel: Ctrl/Cmd + B
      if ((e.metaKey || e.ctrlKey) && e.key === 'b') {
        e.preventDefault();
        toggleLeftPanel();
      }
      // Toggle right panel: Ctrl/Cmd + L
      if ((e.metaKey || e.ctrlKey) && e.key === 'l') {
        e.preventDefault();
        toggleRightPanel();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [toggleLeftPanel, toggleRightPanel]);

  const hasLeftPanel = !!leftPanel;
  const hasRightPanel = !!rightPanel;
  const hasHeader = !!header;
  const hasBottom = !!bottomPanel;

  // Build grid template
  const gridTemplateColumns = [
    hasLeftPanel && !layoutState.leftPanel.collapsed
      ? `${layoutState.leftPanel.width}px`
      : hasLeftPanel ? '40px' : '0px',
    '1fr',
    hasRightPanel && !layoutState.rightPanel.collapsed
      ? `${layoutState.rightPanel.width}px`
      : hasRightPanel ? '40px' : '0px',
  ].join(' ');

  const gridTemplateRows = [
    hasHeader ? 'auto' : '0px',
    '1fr',
    hasBottom ? 'auto' : '0px',
  ].join(' ');

  return (
    <LayoutContext.Provider
      value={{
        state: layoutState,
        updateLeftPanel,
        updateRightPanel,
        toggleLeftPanel,
        toggleRightPanel,
        isInteractingWithUI,
        setIsInteractingWithUI,
      }}
    >
      <div
        className={`app-container ${className}`}
        style={{
          display: 'grid',
          gridTemplateColumns,
          gridTemplateRows,
          width: '100vw',
          height: '100vh',
          overflow: 'hidden',
          pointerEvents: 'none', // Allow clicks to pass through to game canvas
        }}
      >
        {/* Header - spans all columns */}
        {hasHeader && (
          <div
            className="app-header"
            onMouseEnter={() => setIsInteractingWithUI(true)}
            onMouseLeave={() => setIsInteractingWithUI(false)}
            style={{
              gridColumn: '1 / -1',
              gridRow: 1,
              zIndex: 10,
              pointerEvents: 'auto', // Re-enable clicks for UI elements
            }}
          >
            {header}
          </div>
        )}

        {/* Left Panel */}
        {hasLeftPanel && (
          <div
            className="app-left-panel"
            onMouseEnter={() => setIsInteractingWithUI(true)}
            onMouseLeave={() => setIsInteractingWithUI(false)}
            style={{
              gridColumn: 1,
              gridRow: hasHeader ? '2 / 3' : '1 / 3',
              overflow: 'hidden',
              display: 'flex',
              pointerEvents: 'auto', // Re-enable clicks for UI elements
            }}
          >
            {React.isValidElement(leftPanel) ? (
              React.cloneElement(leftPanel as React.ReactElement<any>, {
                isCollapsed: layoutState.leftPanel.collapsed,
                onToggle: toggleLeftPanel,
                onResize: (width: number) => updateLeftPanel({ width }),
                position: 'left',
              })
            ) : (
              <Panel
                position="left"
                isCollapsed={layoutState.leftPanel.collapsed}
                onToggle={toggleLeftPanel}
                onResize={(width) => updateLeftPanel({ width })}
                config={layoutState.leftPanel}
              >
                {leftPanel}
              </Panel>
            )}
          </div>
        )}

        {/* Main Content - No pointer events so clicks pass through to canvas */}
        <div
          className="app-main-content"
          style={{
            gridColumn: hasLeftPanel ? 2 : 1,
            gridRow: hasHeader ? '2 / 3' : '1 / 3',
            position: 'relative',
            overflow: 'hidden',
            pointerEvents: 'none', // Critical for passing clicks down
          }}
        >
          {children}
        </div>

        {/* Right Panel */}
        {hasRightPanel && (
          <div
            className="app-right-panel"
            onMouseEnter={() => setIsInteractingWithUI(true)}
            onMouseLeave={() => setIsInteractingWithUI(false)}
            style={{
              gridColumn: hasLeftPanel ? 3 : 2,
              gridRow: hasHeader ? '2 / 3' : '1 / 3',
              overflow: 'hidden',
              display: 'flex',
              pointerEvents: 'auto', // Re-enable clicks for UI elements
            }}
          >
            {React.isValidElement(rightPanel) ? (
              React.cloneElement(rightPanel as React.ReactElement<any>, {
                isCollapsed: layoutState.rightPanel.collapsed,
                onToggle: toggleRightPanel,
                onResize: (width: number) => updateRightPanel({ width }),
                position: 'right',
              })
            ) : (
              <Panel
                position="right"
                isCollapsed={layoutState.rightPanel.collapsed}
                onToggle={toggleRightPanel}
                onResize={(width) => updateRightPanel({ width })}
                config={layoutState.rightPanel}
              >
                {rightPanel}
              </Panel>
            )}
          </div>
        )}

        {/* Bottom Panel - spans content area */}
        {hasBottom && (
          <div
            className="app-bottom-panel"
            onMouseEnter={() => setIsInteractingWithUI(true)}
            onMouseLeave={() => setIsInteractingWithUI(false)}
            style={{
              gridColumn: hasLeftPanel ? '2 / 3' : '1 / 2',
              gridRow: hasHeader ? 3 : 2,
              zIndex: 10,
              pointerEvents: 'auto', // Re-enable clicks for UI elements
            }}
          >
            {bottomPanel}
          </div>
        )}
      </div>
    </LayoutContext.Provider>
  );
};
